import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  DollarSign,
  TrendingUp,
  Users,
  Clock,
  CheckCircle,
  AlertCircle,
  ChevronLeft,
  Eye,
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import apiService from '@/services/api';
import AdminLayout from '@/components/layout/AdminLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import PaymentDetailsModal from '@/components/admin/PaymentDetailsModal';

interface FinancialSummary {
  period: string;
  summary: {
    total_received_payments: number;
    total_received_gbp: string;
    failed_count?: number;
    pending_count?: number;
    refunded_count?: number;
    note?: string;
  };
  provider_breakdown: Array<{
    provider_id: string;
    provider_name: string;
    payment_count: number;
    total_received_gbp: string;
    wallet_balance_gbp: string;
  }>;
}

interface Payment {
  payment_id: number;
  amount_gbp: string;
  amount_pence: number;
  status: string;
  payment_date: string;
  customer: {
    name: string;
    email: string;
  };
  booking: {
    id: number;
    status: string;
  };
  service: {
    name: string;
  };
}

interface ProviderPayments {
  provider_id: string;
  payment_count: number;
  total_received_gbp: string;
  payments: Payment[];
}

const AdminFinancialDashboard: React.FC = () => {
  const { user } = useAuth();
  const [financialData, setFinancialData] = useState<FinancialSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState<'today' | 'week' | 'month' | 'all'>('today');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  
  // Provider payments view state
  const [selectedProvider, setSelectedProvider] = useState<{ id: string; name: string } | null>(null);
  const [providerPayments, setProviderPayments] = useState<ProviderPayments | null>(null);
  const [loadingPayments, setLoadingPayments] = useState(false);
  
  // Payment details modal state
  const [selectedPaymentId, setSelectedPaymentId] = useState<number | null>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  useEffect(() => {
    loadFinancialSnapshot();
  }, [period]);

  const loadFinancialSnapshot = async () => {
    try {
      setLoading(true);
      const response = await apiService.get(`/admin/financial-snapshot?period=${period}`);
      
      if (response.success && response.data) {
        setFinancialData(response.data);
      } else {
        setMessage({ type: 'error', text: 'Failed to load financial data' });
      }
    } catch (error) {
      console.error('Error loading financial snapshot:', error);
      setMessage({ type: 'error', text: 'Failed to load financial snapshot' });
    } finally {
      setLoading(false);
    }
  };

  const loadProviderPayments = async (providerId: string, providerName: string) => {
    try {
      setLoadingPayments(true);
      setSelectedProvider({ id: providerId, name: providerName });
      const response = await apiService.get(`/admin/payments/provider/${providerId}`);
      if (response.success && response.data) {
        setProviderPayments(response.data);
      } else {
        setMessage({ type: 'error', text: 'Failed to load provider payments' });
      }
    } catch (error) {
      console.error('Error loading provider payments:', error);
      setMessage({ type: 'error', text: 'Failed to load provider payments' });
    } finally {
      setLoadingPayments(false);
    }
  };

  const handleViewPayment = (paymentId: number) => {
    setSelectedPaymentId(paymentId);
    setShowPaymentModal(true);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <p className="text-gray-600">Loading financial data...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Helmet>
        <title>Financial Dashboard - Admin</title>
      </Helmet>

      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Financial Dashboard</h1>
            <p className="text-gray-600 mt-1">Payment tracking and revenue analysis</p>
          </div>

          {/* Period Selector */}
          <div className="flex gap-2">
            <Button
              variant={period === 'today' ? 'default' : 'outline'}
              onClick={() => setPeriod('today')}
              size="sm"
            >
              Today
            </Button>
            <Button
              variant={period === 'week' ? 'default' : 'outline'}
              onClick={() => setPeriod('week')}
              size="sm"
            >
              Week
            </Button>
            <Button
              variant={period === 'month' ? 'default' : 'outline'}
              onClick={() => setPeriod('month')}
              size="sm"
            >
              Month
            </Button>
            <Button
              variant={period === 'all' ? 'default' : 'outline'}
              onClick={() => setPeriod('all')}
              size="sm"
            >
              All Time
            </Button>
          </div>
        </div>

        {/* Success/Error Message */}
        {message && (
          <div
            className={`p-4 rounded-lg ${
              message.type === 'success'
                ? 'bg-green-50 text-green-800 border border-green-200'
                : 'bg-red-50 text-red-800 border border-red-200'
            }`}
          >
            {message.text}
          </div>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Total Payments */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Total Payments</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
            <div className="text-2xl font-bold">{financialData?.summary?.total_received_payments || 0}</div>
            <p className="text-xs text-muted-foreground mt-1">payments received (verified)</p>
            </CardContent>
          </Card>

          {/* Total Revenue */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
              <DollarSign className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">£{financialData?.summary?.total_received_gbp || '0.00'}</div>
              <p className="text-xs text-muted-foreground mt-1">all payments received</p>
            </CardContent>
          </Card>
        </div>

        {/* Provider Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Provider Payment Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            {financialData?.provider_breakdown && financialData.provider_breakdown.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Provider</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Payments</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-gray-900">Earned</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-gray-900">Wallet Balance</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {financialData.provider_breakdown.map((provider, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="h-8 w-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center text-white text-xs font-bold">
                              {provider.provider_name.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{provider.provider_name}</p>
                              <p className="text-xs text-gray-500">ID: {provider.provider_id}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant="outline">{provider.payment_count}</Badge>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <p className="font-semibold text-gray-900">£{provider.total_received_gbp}</p>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <p className="font-bold text-green-600">£{provider.wallet_balance_gbp}</p>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => loadProviderPayments(provider.provider_id, provider.provider_name)}
                          >
                            View
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8">
                <AlertCircle className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-500">No payment data available for this period</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Summary Info */}
        <Card className="bg-gradient-to-r from-gray-900 to-gray-800 text-white">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-white">
              <CheckCircle className="h-5 w-5 text-green-400" />
              Period Summary
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-300">Reporting Period:</span>
              <span className="font-semibold capitalize">{financialData?.period || 'Today'}</span>
            </div>
            
            {/* Received Payments */}
            <div className="flex justify-between">
              <span className="text-gray-300">✅ Received Payments:</span>
              <span className="font-semibold text-green-400">{financialData?.summary?.total_received_payments || 0}</span>
            </div>

            {/* Other Payment Statuses */}
            <div className="bg-gray-700/30 p-2 rounded text-sm space-y-1">
              {financialData?.summary?.pending_count ? (
                <div className="flex justify-between text-gray-300">
                  <span>⏳ Pending:</span>
                  <span>{financialData.summary.pending_count}</span>
                </div>
              ) : null}
              {financialData?.summary?.failed_count ? (
                <div className="flex justify-between text-red-300">
                  <span>❌ Failed:</span>
                  <span>{financialData.summary.failed_count}</span>
                </div>
              ) : null}
              {financialData?.summary?.refunded_count ? (
                <div className="flex justify-between text-orange-300">
                  <span>🔄 Refunded:</span>
                  <span>{financialData.summary.refunded_count}</span>
                </div>
              ) : null}
            </div>

            {/* Average */}
            <div className="border-t border-gray-700 pt-3 mt-3 flex justify-between">
              <span className="text-gray-300">Average Received:</span>
              <span className="font-semibold text-accent">
                ${financialData?.summary?.total_received_payments && financialData.summary.total_received_payments > 0
                  ? (parseFloat(financialData.summary.total_received_gbp) / financialData.summary.total_received_payments).toFixed(2)
                  : '0.00'
                }
              </span>
            </div>

            {/* Note */}
            <div className="bg-blue-900/30 p-2 rounded text-xs text-blue-200 italic mt-2">
              {financialData?.summary?.note || '📊 Only received (verified) payments are included in revenue calculations'}
            </div>
          </CardContent>
        </Card>

        {/* Provider Payments Details View */}
        {selectedProvider && (
          <>
            {/* Back Button */}
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setSelectedProvider(null);
                  setProviderPayments(null);
                }}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Back to Overview
              </Button>
              {selectedProvider && (
                <h2 className="text-xl font-semibold text-gray-900">
                  {selectedProvider.name} - Payment Details
                </h2>
              )}
            </div>

            {/* Payments List */}
            {loadingPayments ? (
              <Card>
                <CardContent className="py-8">
                  <div className="text-center">
                    <p className="text-gray-600">Loading payments...</p>
                  </div>
                </CardContent>
              </Card>
            ) : providerPayments && providerPayments.payments.length > 0 ? (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <DollarSign className="h-5 w-5" />
                    All Payments ({providerPayments.payment_count})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Payment ID</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Date</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Customer</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Service</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Booking ID</th>
                          <th className="px-4 py-3 text-right text-sm font-semibold text-gray-900">Amount</th>
                          <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900">Status</th>
                          <th className="px-4 py-3 text-center text-sm font-semibold text-gray-900">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {providerPayments.payments.map((payment) => (
                          <tr key={payment.payment_id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 font-mono text-sm text-gray-900">
                              #{payment.payment_id}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900">
                              {new Date(payment.payment_date).toLocaleDateString()} {new Date(payment.payment_date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-900">
                              <div>
                                <p className="font-medium">{payment.customer.name}</p>
                                <p className="text-xs text-gray-600">{payment.customer.email}</p>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm">
                              <div>
                                <p className="font-medium text-gray-900">{payment.service.name}</p>
                              </div>
                            </td>
                            <td className="px-4 py-3 font-mono text-sm text-gray-600">
                              #{payment.booking.id}
                            </td>
                            <td className="px-4 py-3 text-right font-semibold text-green-600">
                              ${payment.amount_gbp}
                            </td>
                            <td className="px-4 py-3 text-center">
                              {payment.status === 'succeeded' ? (
                                <Badge variant="default" className="bg-green-600">
                                  ✓ Succeeded
                                </Badge>
                              ) : (
                                <Badge variant="outline">{payment.status}</Badge>
                              )}
                            </td>
                            <td className="px-4 py-3 text-center">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleViewPayment(payment.payment_id)}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Summary Footer */}
                  <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex justify-between items-center">
                      <span className="font-semibold text-gray-900">Total for This Provider:</span>
                      <span className="text-2xl font-bold text-green-600">£{providerPayments.total_received_gbp}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="py-8">
                  <div className="text-center">
                    <AlertCircle className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-500">No payments found for this provider</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>

      {/* Payment Details Modal */}
      <PaymentDetailsModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        paymentId={selectedPaymentId}
      />
    </AdminLayout>
  );
};

export default AdminFinancialDashboard;
